import { defineStore } from 'pinia';
import { shouldThrottle } from '@/util/rateLimit.js';
import apiClient from '@/util/apiClient';
import posthog from '@/composables/usePostHog.js';
import { reportError } from '@/services/errorReporting.ts';
import EGS from '@/util/egs_util.ts';
import { stores } from '@/util/stores.js';

export const THROTTLED_RESULT = Object.freeze({ success: false, reason: 'throttled', data: null });
const API_ERROR_RESULT = Object.freeze({ success: false, reason: 'api_error', data: null });

/**
 * Upload context configurations defining allowed file types, sizes, and counts.
 * Each context represents a specific upload use case in the application.
 * Must stay in sync with the backend FileRouteContext enum.
 *
 * @type {Object.<string, {allowedTypes: string[], allowedExtensions: string[], maxSizeBytes: number, maxFiles: number, label: string, acceptAttr: string}>}
 */
export const UPLOAD_CONTEXTS = {
	TICKET_ATTACHMENT: {
		allowedTypes: [
			'image/jpeg',
			'image/png',
			'image/gif',
			'image/webp',
			'application/pdf',
			'application/msword',
			'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
			'application/vnd.ms-excel',
			'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
			'application/vnd.ms-powerpoint',
			'application/vnd.openxmlformats-officedocument.presentationml.presentation',
			'text/plain',
			'text/csv'
		],
		allowedExtensions: ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx', '.txt', '.csv'],
		maxSizeBytes: 10 * 1024 * 1024, // 10MB
		maxFiles: 5,
		label: 'Ticket Attachment',
		acceptAttr:
			'image/jpeg,image/png,image/gif,image/webp,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,text/plain,text/csv,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.pdf,.txt,.csv'
	},
	PROFILE_PICTURE: {
		allowedTypes: ['image/jpeg', 'image/png', 'image/webp'],
		allowedExtensions: ['.jpg', '.jpeg', '.png', '.webp'],
		maxSizeBytes: 5 * 1024 * 1024, // 5MB
		maxFiles: 1,
		label: 'Profile Picture',
		acceptAttr: 'image/jpeg,image/png,image/webp'
	},
	DOCUMENT: {
		allowedTypes: ['application/pdf', 'image/jpeg', 'image/png'],
		allowedExtensions: ['.pdf', '.jpg', '.jpeg', '.png'],
		maxSizeBytes: 20 * 1024 * 1024, // 20MB
		maxFiles: 10,
		label: 'Document',
		acceptAttr: 'application/pdf,image/jpeg,image/png'
	}
};

/**
 * Pinia store for managing file upload operations.
 * Handles presigning, association, listing, and deletion of uploaded files
 * through the backend API.
 *
 * @module fileUploadStore
 */
export const useFileUploadStore = defineStore('fileUploadStore', {
	state: () => ({
		/** @type {Array<Object>} List of files associated with the current entity */
		files: [],
		/** @type {boolean} Whether a file operation is in progress */
		isLoading: false,
		/** @type {string|null} Current error message */
		error: null
	}),
	actions: {
		/**
		 * Request presigned URLs from the backend for file uploads.
		 *
		 * @param {Array<{name: string, size: number, type: string}>} fileMetas - Array of file metadata objects
		 * @param {string} context - Upload context (e.g., 'TICKET_ATTACHMENT')
		 * @returns {Promise<{files: Array<{fileId: number, fileKey: string, presignedUrl: string, fileName: string}>}>} Presigned URL data
		 */
		async presignFiles(fileMetas, context) {
			if (shouldThrottle('presignFiles', 100)) {
				posthog.capture('throttled_action', { action: 'presignFiles' });
				return THROTTLED_RESULT;
			}
			const response = await apiClient.post('/files/presign', {
				files: fileMetas,
				context
			});
			return response.data;
		},

		/**
		 * Associate an uploaded file with a parent entity (e.g., ticket, comment).
		 *
		 * @param {number} fileId - The file ID returned from the presign step
		 * @param {string} entityType - The type of entity to associate with (e.g., 'TICKET', 'TICKET_COMMENT')
		 * @param {number} entityId - The ID of the parent entity
		 * @returns {Promise<Object>} Updated file metadata
		 */
		async associateFile(fileId, entityType, entityId) {
			if (shouldThrottle(`associateFile_${fileId}`, 100)) {
				posthog.capture('throttled_action', { action: 'associateFile' });
				return THROTTLED_RESULT;
			}
			try {
				const response = await apiClient.post(`/files/${fileId}/associate`, {
					entityType,
					entityId
				});
				return response.data;
			} catch (err) {
				reportError(err, {
					source: 'file_store',
					domain: entityType,
					operation: 'associate_file',
					handled: true,
					details: { file_id: fileId, entity_id: entityId }
				});
				return API_ERROR_RESULT;
			}
		},
		async associateFiles(files, entityType, entityId) {
			if (!Array.isArray(files) || files.length === 0) {
				return { success: true, failedCount: 0, results: [] };
			}

			const associationTargets = files.filter((file) => file?.fileId !== null && file?.fileId !== undefined);
			const results = await Promise.allSettled(associationTargets.map((file) => this.associateFile(file.fileId, entityType, entityId)));
			const failedCount = results.filter((result) => {
				if (result.status === 'rejected') return true;
				return result.value?.success === false;
			}).length;

			return {
				success: failedCount === 0,
				failedCount,
				results
			};
		},

		/**
		 * Fetch files associated with a specific entity.
		 * Backend returns a Spring Page; this extracts the content array.
		 *
		 * @param {string} entityType - The type of entity (e.g., 'TICKET', 'TICKET_COMMENT')
		 * @param {number} entityId - The ID of the entity
		 * @returns {Promise<Array<Object>>} List of associated file objects
		 */
		async fetchFiles(entityType, entityId) {
			if (shouldThrottle(`fetchFiles_${entityType}_${entityId}`, 100)) {
				posthog.capture('throttled_action', { action: 'fetchFiles' });
				return this.files;
			}
			this.isLoading = true;
			this.error = null;
			try {
				const response = await apiClient.get('/files', {
					params: { entityType, entityId }
				});
				this.files = response.data.content || [];
				return this.files;
			} catch (err) {
				this.error = err.response?.data?.error || err.message || 'Failed to fetch files';
				reportError(err, {
					source: 'file_store',
					domain: entityType,
					operation: 'fetch_files',
					handled: true,
					details: { entity_id: entityId }
				});
				return [];
			} finally {
				this.isLoading = false;
			}
		},

		/**
		 * Delete a file by its ID.
		 *
		 * @param {number} fileId - The file ID to delete
		 * @returns {Promise<void>}
		 */
		async deleteFile(fileId) {
			if (shouldThrottle(`deleteFile_${fileId}`, 100)) {
				posthog.capture('throttled_action', { action: 'deleteFile' });
				return;
			}
			try {
				await apiClient.delete(`/files/${fileId}`);
				this.files = this.files.filter((f) => f.fileId !== fileId);
			} catch (err) {
				reportError(err, {
					source: 'file_store',
					domain: 'file',
					operation: 'delete_file',
					handled: false,
					details: { file_id: fileId }
				});
				throw err;
			}
		},
		async saveProfilePhoto(userNumber, uploadedFile) {
			if (shouldThrottle('saveProfilePhoto', 100)) {
				posthog.capture('throttled_action', { action: 'saveProfilePhoto' });
				return { success: false, message: 'Request throttled' };
			}
			if (!uploadedFile?.fileId) {
				return { success: false, message: 'Uploaded file metadata is missing' };
			}

			const fileUploadStore = useFileUploadStore();
			const associateResponse = await fileUploadStore.associateFile(uploadedFile.fileId, 'USER_PROFILE', userNumber);
			const photoReference = uploadedFile.fileKey || associateResponse?.fileKey || associateResponse?.fileUrl;
			if (!photoReference) {
				return { success: false, message: 'Photo uploaded but no display URL was returned yet' };
			}

			const result = await stores.usersStore.updateUser(userNumber, { photo: photoReference });
			if (!result?.success) {
				return { success: false, message: result?.message || 'Failed to save profile picture' };
			}
			return { success: true, data: result.data, photoReference };
		},
		async removeUserPhoto(userNumber) {
			if (shouldThrottle('removeUserPhoto', 100)) {
				posthog.capture('throttled_action', { action: 'removeUserPhoto' });
				return { success: false, message: 'Request throttled' };
			}
			try {
				const response = await apiClient.delete(`/users/edit/${userNumber}/photo`);
				if (response.status === 200) {
					EGS.UserInformation.syncSessionUserInfo(response.data);
					return { success: true, data: response.data };
				}
				return { success: false, message: 'Failed to remove user photo' };
			} catch (e) {
				return { success: false, message: e.response?.data?.message || 'Photo removal failed' };
			}
		}
	}
});
